/**
 * Page model builders — the single place where "real marketplace data" becomes
 * "everything a public page needs".
 *
 * A page model carries the metadata (head tags), the breadcrumb trail, the
 * visible intro copy, the stylist results, the internal-link blocks and the
 * JSON-LD graph. Both the Node prerenderer and the React pages build their page
 * from one of these, so head tags, visible copy and structured data are always
 * generated from the same numbers.
 *
 * Internal linking follows real inventory only: a city page links to the
 * service pages that actually have supply in that city, never to all 13
 * categories x every city.
 *
 * CommonJS on purpose — shared by the CRA bundle and the Node build scripts.
 */

const { ROUTES } = require('./urls');
const { resolveState, US_STATES } = require('./states');
const { resolveServiceCategory, SERVICE_CATEGORIES } = require('./taxonomy');
const metadataEngine = require('./metadata');
const eligibility = require('./eligibility');
const structuredData = require('./structuredData');

const { formatPlace } = metadataEngine;

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

/**
 * Which stylists may appear as cards on a public page.
 *
 * The backend's public projection already applies the discovery gate, so this
 * is defence in depth rather than the primary control: a profile that is not
 * approved, not email-verified, deleted, or has no public URL must never be
 * rendered on a public page even if a caller hands one over.
 *
 * Note this is a VISIBILITY filter, not the index-eligibility filter. A live
 * profile that is merely thin (no bio yet) still appears in listings for
 * humans; it just does not get indexed itself.
 *
 * @param {Array<Object>} stylists
 */
function listableStylists(stylists) {
  return toArray(stylists).filter(
    (s) =>
      s &&
      s.seoSlug &&
      s.displayName &&
      s.isDeleted !== true &&
      s.deletionRequested !== true &&
      s.emailVerified !== false &&
      (!s.approvalStatus || s.approvalStatus === 'approved')
  );
}

function positiveInt(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/**
 * Platform-level JSON-LD every public page carries.
 * @param {Object} ctx
 * @param {Array<{ name: string, path: string }>} breadcrumbs
 */
function platformGraph(ctx, breadcrumbs) {
  return [
    structuredData.organizationSchema(ctx),
    structuredData.webSiteSchema(ctx),
    structuredData.breadcrumbSchema(breadcrumbs, ctx),
  ].filter(Boolean);
}

/* ------------------------------------------------------------------ *
 * /stylists — national directory
 * ------------------------------------------------------------------ */

/**
 * @param {Object} data  { stylists, totals, topStates, topServices }
 * @param {{ environment?: string, siteUrl?: string }} [ctx]
 */
function buildStylistDirectoryModel(data, ctx) {
  const d = data || {};
  const c = ctx || {};
  const totals = d.totals || {};

  const metadata = metadataEngine.stylistDirectoryMetadata({
    stylistCount: totals.stylistCount,
    cityCount: totals.cityCount,
    stateCount: totals.stateCount,
    environment: c.environment,
    siteUrl: c.siteUrl,
  });

  const breadcrumbs = [
    { name: 'LookBel', path: ROUTES.home() },
    { name: 'Professionals', path: ROUTES.stylistDirectory() },
  ];

  const stylists = listableStylists(d.stylists);

  const linkGroups = [
    {
      heading: 'Browse by service',
      description: 'Service categories with professionals currently taking bookings.',
      items: toArray(d.topServices)
        .map((row) => {
          const category = resolveServiceCategory(row.serviceCategory);
          if (!category) return null;
          const count = positiveInt(row.stylistCount);
          return {
            label: category.providerNoun,
            path: ROUTES.service(category.slug),
            meta: count ? `${count} pro${count === 1 ? '' : 's'}` : undefined,
          };
        })
        .filter(Boolean),
    },
    {
      heading: 'Browse by state',
      description: 'States where LookBel professionals are live today.',
      items: toArray(d.topStates)
        .map((row) => {
          const state = resolveState(row.state);
          if (!state) return null;
          const count = positiveInt(row.stylistCount);
          return {
            label: state.name,
            path: ROUTES.state(state.slug),
            meta: count ? `${count} pro${count === 1 ? '' : 's'}` : undefined,
          };
        })
        .filter(Boolean),
    },
  ];

  return {
    kind: 'stylist-directory',
    metadata,
    breadcrumbs,
    intro: metadata.description,
    stylists,
    stylistListHeading: 'Featured professionals',
    emptyState: {
      heading: 'No professionals are listed yet',
      body: 'LookBel is onboarding independent beauty and wellness professionals across the United States.',
      ctaLabel: 'Join LookBel as a professional',
      ctaPath: '/register',
    },
    linkGroups,
    jsonLd: platformGraph(c, breadcrumbs).concat(
      [structuredData.stylistItemListSchema(stylists, { ...c, name: 'Featured professionals' })].filter(
        Boolean
      )
    ),
  };
}

/* ------------------------------------------------------------------ *
 * /locations — state index hub
 * ------------------------------------------------------------------ */

/**
 * The national location hub. Lists only states with live inventory as primary
 * links; the remaining states are reachable but are not promoted as a wall of
 * empty links.
 */
function buildLocationIndexModel(data, ctx) {
  const d = data || {};
  const c = ctx || {};
  const liveStates = toArray(d.states)
    .map((row) => {
      const state = resolveState(row.state);
      if (!state) return null;
      return { state, stylistCount: positiveInt(row.stylistCount), cities: toArray(row.cities) };
    })
    .filter(Boolean);

  const metadata = metadataEngine.buildMetadata({
    title: metadataEngine.withSuffix('Find Beauty & Wellness Pros by State'),
    description: liveStates.length
      ? `LookBel professionals are live in ${liveStates.length} state${
          liveStates.length === 1 ? '' : 's'
        }. Pick a state to see cities and book online.`
      : 'Browse LookBel beauty and wellness professionals by state and city across the United States.',
    path: ROUTES.locationIndex(),
    h1: 'Find beauty and wellness professionals by state',
    indexable: liveStates.length > 0,
    environment: c.environment,
    siteUrl: c.siteUrl,
  });

  const breadcrumbs = [
    { name: 'LookBel', path: ROUTES.home() },
    { name: 'Locations', path: ROUTES.locationIndex() },
  ];

  return {
    kind: 'location-index',
    metadata,
    breadcrumbs,
    intro: metadata.description,
    showStylistList: false,
    stylists: [],
    linkGroups: [
      {
        heading: 'States with live professionals',
        items: liveStates.map((row) => ({
          label: row.state.name,
          path: ROUTES.state(row.state.slug),
          meta: row.stylistCount ? `${row.stylistCount} pro${row.stylistCount === 1 ? '' : 's'}` : undefined,
        })),
      },
      {
        heading: 'All states and DC',
        description:
          'LookBel supports every US state. States without live professionals yet are not indexed until real availability exists.',
        items: US_STATES.map((state) => ({
          label: state.name,
          path: ROUTES.state(state.slug),
        })),
      },
    ],
    jsonLd: platformGraph(c, breadcrumbs),
  };
}

/* ------------------------------------------------------------------ *
 * /services/:category — national service hub
 * ------------------------------------------------------------------ */

function buildServiceHubModel(data, ctx) {
  const d = data || {};
  const c = ctx || {};
  const category = resolveServiceCategory(d.serviceCategory);
  if (!category) return null;

  const verdict = eligibility.evaluateServiceHub({
    serviceCategory: category.slug,
    stylistCount: d.stylistCount,
  });

  const metadata = metadataEngine.serviceHubMetadata({
    serviceCategory: category.slug,
    stylistCount: d.stylistCount,
    cityCount: d.cityCount,
    priceMin: d.priceMin,
    priceMax: d.priceMax,
    indexable: verdict.indexable,
    environment: c.environment,
    siteUrl: c.siteUrl,
  });

  const breadcrumbs = [
    { name: 'LookBel', path: ROUTES.home() },
    { name: 'Services', path: ROUTES.serviceIndex() },
    { name: category.label, path: ROUTES.service(category.slug) },
  ];

  const stylists = listableStylists(d.stylists);

  return {
    kind: 'service-hub',
    metadata,
    eligibility: verdict,
    breadcrumbs,
    intro: metadata.description,
    stylists,
    stylistListHeading: `${category.providerNoun} taking bookings`,
    emptyState: {
      heading: `No ${category.providerNoun.toLowerCase()} are listed yet`,
      body: `LookBel is onboarding ${category.providerNoun.toLowerCase()} across the United States. Browse other services in the meantime.`,
      ctaLabel: 'Browse all professionals',
      ctaPath: ROUTES.stylistDirectory(),
    },
    linkGroups: [
      {
        heading: `${category.label} by city`,
        description: `Cities with ${category.providerNoun.toLowerCase()} currently available.`,
        items: toArray(d.cities)
          .map((row) => {
            const state = resolveState(row.state);
            if (!state || !row.city) return null;
            const count = positiveInt(row.stylistCount);
            return {
              label: `${category.providerNoun} in ${formatPlace(row.city, state.code)}`,
              path: ROUTES.cityService(state.slug, row.city, category.slug),
              meta: count ? `${count} pro${count === 1 ? '' : 's'}` : undefined,
            };
          })
          .filter(Boolean),
      },
      {
        heading: 'Other services on LookBel',
        items: SERVICE_CATEGORIES.filter((other) => other.slug !== category.slug).map((other) => ({
          label: other.providerNoun,
          path: ROUTES.service(other.slug),
        })),
      },
    ],
    jsonLd: platformGraph(c, breadcrumbs)
      .concat(
        [
          structuredData.serviceCategorySchema(
            { serviceCategory: category.slug, priceMin: d.priceMin, priceMax: d.priceMax },
            c
          ),
        ].filter(Boolean)
      )
      .concat(
        [
          structuredData.stylistItemListSchema(stylists, {
            ...c,
            name: `${category.providerNoun} on LookBel`,
          }),
        ].filter(Boolean)
      ),
  };
}

/* ------------------------------------------------------------------ *
 * /locations/:state
 * ------------------------------------------------------------------ */

function buildStateModel(data, ctx) {
  const d = data || {};
  const c = ctx || {};
  const state = resolveState(d.state);
  if (!state) return null;

  const cities = toArray(d.cities);
  const verdict = eligibility.evaluateState({
    state: state.code,
    stylistCount: d.stylistCount,
    cities,
  });

  const metadata = metadataEngine.stateMetadata({
    state: state.code,
    stylistCount: d.stylistCount,
    cityCount: cities.length,
    indexable: verdict.indexable,
    environment: c.environment,
    siteUrl: c.siteUrl,
  });

  const breadcrumbs = [
    { name: 'LookBel', path: ROUTES.home() },
    { name: 'Locations', path: ROUTES.locationIndex() },
    { name: state.name, path: ROUTES.state(state.slug) },
  ];

  const stylists = listableStylists(d.stylists);

  return {
    kind: 'state',
    metadata,
    eligibility: verdict,
    breadcrumbs,
    intro: metadata.description,
    stylists,
    stylistListHeading: `Professionals serving ${state.name}`,
    emptyState: {
      heading: `LookBel is not live in ${state.name} yet`,
      body: `No professionals have listed in ${state.name} so far. If you work in ${state.name}, you can be the first.`,
      ctaLabel: `Be the first professional in ${state.name}`,
      ctaPath: '/register',
    },
    linkGroups: [
      {
        heading: `Cities in ${state.name}`,
        description: `Cities where LookBel professionals are currently available in ${state.name}.`,
        items: cities
          .map((row) => {
            if (!row.city) return null;
            const count = positiveInt(row.stylistCount);
            return {
              label: formatPlace(row.city, state.code),
              path: ROUTES.city(state.slug, row.city),
              meta: count ? `${count} pro${count === 1 ? '' : 's'}` : undefined,
            };
          })
          .filter(Boolean),
      },
      {
        heading: 'Browse other states',
        items: toArray(d.siblingStates)
          .map((row) => {
            const sibling = resolveState(row.state);
            if (!sibling || sibling.code === state.code) return null;
            return { label: sibling.name, path: ROUTES.state(sibling.slug) };
          })
          .filter(Boolean),
      },
    ],
    jsonLd: platformGraph(c, breadcrumbs).concat(
      [
        structuredData.stylistItemListSchema(stylists, {
          ...c,
          name: `Professionals in ${state.name}`,
        }),
      ].filter(Boolean)
    ),
  };
}

/* ------------------------------------------------------------------ *
 * /locations/:state/:city
 * ------------------------------------------------------------------ */

function buildCityModel(data, ctx) {
  const d = data || {};
  const c = ctx || {};
  const state = resolveState(d.state);
  if (!state || !d.city) return null;

  const verdict = eligibility.evaluateCity({
    city: d.city,
    state: state.code,
    stylistCount: d.stylistCount,
  });

  const metadata = metadataEngine.cityMetadata({
    city: d.city,
    state: state.code,
    stylistCount: d.stylistCount,
    priceMin: d.priceMin,
    priceMax: d.priceMax,
    indexable: verdict.indexable,
    environment: c.environment,
    siteUrl: c.siteUrl,
  });

  const place = formatPlace(d.city, state.code);
  const breadcrumbs = [
    { name: 'LookBel', path: ROUTES.home() },
    { name: 'Locations', path: ROUTES.locationIndex() },
    { name: state.name, path: ROUTES.state(state.slug) },
    { name: d.city, path: ROUTES.city(state.slug, d.city) },
  ];

  const stylists = listableStylists(d.stylists);

  return {
    kind: 'city',
    metadata,
    eligibility: verdict,
    breadcrumbs,
    intro: metadata.description,
    stylists,
    stylistListHeading: `Professionals in ${place}`,
    emptyState: {
      heading: `No professionals in ${place} yet`,
      body: `LookBel has not onboarded a professional in ${place} yet. Browse ${state.name} for nearby options, or list your own services.`,
      ctaLabel: `Be the first professional in ${d.city}`,
      ctaPath: '/register',
    },
    linkGroups: [
      {
        heading: `Services available in ${place}`,
        description: 'Only service categories with real availability in this city are listed.',
        items: toArray(d.services)
          .map((row) => {
            const category = resolveServiceCategory(row.serviceCategory);
            if (!category) return null;
            const count = positiveInt(row.stylistCount);
            return {
              label: `${category.providerNoun} in ${place}`,
              path: ROUTES.cityService(state.slug, d.city, category.slug),
              meta: count ? `${count} pro${count === 1 ? '' : 's'}` : undefined,
            };
          })
          .filter(Boolean),
      },
      {
        heading: `Nearby cities in ${state.name}`,
        items: toArray(d.nearbyCities)
          .map((row) => {
            if (!row.city) return null;
            return {
              label: formatPlace(row.city, state.code),
              path: ROUTES.city(state.slug, row.city),
            };
          })
          .filter(Boolean),
      },
      {
        heading: 'Go broader',
        items: [
          { label: `All professionals in ${state.name}`, path: ROUTES.state(state.slug) },
          { label: 'All LookBel professionals', path: ROUTES.stylistDirectory() },
        ],
      },
    ],
    jsonLd: platformGraph(c, breadcrumbs).concat(
      [structuredData.stylistItemListSchema(stylists, { ...c, name: `Professionals in ${place}` })].filter(
        Boolean
      )
    ),
  };
}

/* ------------------------------------------------------------------ *
 * /locations/:state/:city/:service
 * ------------------------------------------------------------------ */

function buildCityServiceModel(data, ctx) {
  const d = data || {};
  const c = ctx || {};
  const state = resolveState(d.state);
  const category = resolveServiceCategory(d.serviceCategory);
  if (!state || !category || !d.city) return null;

  const verdict = eligibility.evaluateCityService({
    city: d.city,
    state: state.code,
    serviceCategory: category.slug,
    stylistCount: d.stylistCount,
  });

  const metadata = metadataEngine.cityServiceMetadata({
    city: d.city,
    state: state.code,
    serviceCategory: category.slug,
    stylistCount: d.stylistCount,
    priceMin: d.priceMin,
    priceMax: d.priceMax,
    indexable: verdict.indexable,
    environment: c.environment,
    siteUrl: c.siteUrl,
  });

  const place = formatPlace(d.city, state.code);
  const breadcrumbs = [
    { name: 'LookBel', path: ROUTES.home() },
    { name: 'Locations', path: ROUTES.locationIndex() },
    { name: state.name, path: ROUTES.state(state.slug) },
    { name: d.city, path: ROUTES.city(state.slug, d.city) },
    { name: category.label, path: ROUTES.cityService(state.slug, d.city, category.slug) },
  ];

  const stylists = listableStylists(d.stylists);

  return {
    kind: 'city-service',
    metadata,
    eligibility: verdict,
    breadcrumbs,
    intro: metadata.description,
    stylists,
    stylistListHeading: `${category.providerNoun} in ${place}`,
    emptyState: {
      heading: `No ${category.providerNoun.toLowerCase()} in ${place} yet`,
      body: `We have not onboarded ${category.providerNoun.toLowerCase()} in ${place}. See every professional in ${place}, or list your own ${category.serviceNoun} services.`,
      ctaLabel: `Be the first ${category.providerNoun.replace(/s$/, '').toLowerCase()} in ${d.city}`,
      ctaPath: '/register',
    },
    linkGroups: [
      {
        heading: `Other services in ${place}`,
        items: toArray(d.siblingServices)
          .map((row) => {
            const other = resolveServiceCategory(row.serviceCategory);
            if (!other || other.slug === category.slug) return null;
            const count = positiveInt(row.stylistCount);
            return {
              label: `${other.providerNoun} in ${place}`,
              path: ROUTES.cityService(state.slug, d.city, other.slug),
              meta: count ? `${count} pro${count === 1 ? '' : 's'}` : undefined,
            };
          })
          .filter(Boolean),
      },
      {
        heading: `${category.providerNoun} in other cities`,
        items: toArray(d.siblingCities)
          .map((row) => {
            const rowState = resolveState(row.state || state.code);
            if (!rowState || !row.city) return null;
            return {
              label: `${category.providerNoun} in ${formatPlace(row.city, rowState.code)}`,
              path: ROUTES.cityService(rowState.slug, row.city, category.slug),
            };
          })
          .filter(Boolean),
      },
      {
        heading: 'Go broader',
        items: [
          { label: `All professionals in ${place}`, path: ROUTES.city(state.slug, d.city) },
          { label: `${category.providerNoun} nationwide`, path: ROUTES.service(category.slug) },
        ],
      },
    ],
    jsonLd: platformGraph(c, breadcrumbs).concat(
      [
        structuredData.stylistItemListSchema(stylists, {
          ...c,
          name: `${category.providerNoun} in ${place}`,
        }),
      ].filter(Boolean)
    ),
  };
}

/* ------------------------------------------------------------------ *
 * /stylists/:seoSlug
 * ------------------------------------------------------------------ */

function buildStylistProfileModel(data, ctx) {
  const d = data || {};
  const c = ctx || {};
  if (!d.seoSlug || !d.displayName) return null;

  const verdict = eligibility.evaluateStylistProfile(d);
  const state = resolveState(d.state);
  const category = resolveServiceCategory(d.primaryCategory);

  const metadata = metadataEngine.stylistProfileMetadata({
    seoSlug: d.seoSlug,
    displayName: d.displayName,
    city: d.city,
    state: d.state,
    primaryCategory: d.primaryCategory,
    shortBio: d.shortBio,
    ratingAverage: d.ratingAverage,
    ratingCount: d.ratingCount,
    priceMin: d.priceMin,
    priceMax: d.priceMax,
    profilePhotoUrl: d.profilePhotoUrl,
    indexable: verdict.indexable,
    environment: c.environment,
    siteUrl: c.siteUrl,
  });

  const place = formatPlace(d.city, d.state);
  const breadcrumbs = [{ name: 'LookBel', path: ROUTES.home() }];
  if (state) {
    breadcrumbs.push({ name: 'Locations', path: ROUTES.locationIndex() });
    breadcrumbs.push({ name: state.name, path: ROUTES.state(state.slug) });
    if (d.city) breadcrumbs.push({ name: d.city, path: ROUTES.city(state.slug, d.city) });
  } else {
    breadcrumbs.push({ name: 'Professionals', path: ROUTES.stylistDirectory() });
  }
  breadcrumbs.push({ name: d.displayName, path: ROUTES.stylistProfile(d.seoSlug) });

  /** @type {Array<{ heading: string, items: Array<Object> }>} */
  const linkGroups = [];
  if (state && d.city && category) {
    linkGroups.push({
      heading: 'Similar professionals nearby',
      items: [
        {
          label: `${category.providerNoun} in ${place}`,
          path: ROUTES.cityService(state.slug, d.city, category.slug),
        },
        { label: `All professionals in ${place}`, path: ROUTES.city(state.slug, d.city) },
        { label: `${category.providerNoun} nationwide`, path: ROUTES.service(category.slug) },
      ],
    });
  }

  return {
    kind: 'stylist-profile',
    metadata,
    eligibility: verdict,
    breadcrumbs,
    intro: null,
    showStylistList: false,
    stylists: [],
    profile: d,
    linkGroups,
    jsonLd: platformGraph(c, breadcrumbs).concat(
      [structuredData.stylistProfileSchema(d, c)].filter(Boolean)
    ),
  };
}

module.exports = {
  platformGraph,
  buildStylistDirectoryModel,
  buildLocationIndexModel,
  buildServiceHubModel,
  buildStateModel,
  buildCityModel,
  buildCityServiceModel,
  buildStylistProfileModel,
};
